import React, { useState } from 'react';
import {
    TextField,
    Button,
    Box,
    Typography,
    Grid,
    IconButton,
    InputAdornment,
    Alert // Import Alert for showing messages
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import config from './config.js';

function Register() {
    const [email, setEmail] = useState('');
    // const [name, setName] = useState(''); // Removed name state
    const [firstName, setFirstName] = useState(''); // Added firstName state
    const [lastName, setLastName] = useState(''); // Added lastName state
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [registrationMessage, setRegistrationMessage] = useState(''); // For success/error messages
    const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submission
    const navigate = useNavigate();

    // Function to check password strength (remains the same)
    const checkPasswordStrength = (password) => {
        const minLength = 8;
        const hasUpperCase = /[A-Z]/.test(password);
        const hasLowerCase = /[a-z]/.test(password);
        const hasNumbers = /\d/.test(password);
        const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);

        if (password.length < minLength) return "Password must be at least 8 characters long.";
        if (!hasUpperCase) return "Password must contain at least one uppercase letter.";
        if (!hasLowerCase) return "Password must contain at least one lowercase letter.";
        if (!hasNumbers) return "Password must contain at least one number.";
        if (!hasSpecialChars) return "Password must contain at least one special character.";
        return "";
    };

    // Function to validate email format (remains the same)
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email) ? "" : "Invalid email format";
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setRegistrationMessage(''); // Clear previous messages
        setIsSubmitting(true); // Disable button

        // Re-validate all fields on submit, just in case
        const currentPasswordError = checkPasswordStrength(password);
        const currentEmailError = validateEmail(email);
        const currentConfirmPasswordError = password !== confirmPassword ? "Passwords do not match." : "";
        const currentFirstNameError = !firstName ? "First name is required." : ""; // Add validation
        const currentLastNameError = !lastName ? "Last name is required." : "";   // Add validation

        setPasswordError(currentPasswordError);
        setEmailError(currentEmailError);
        setConfirmPasswordError(currentConfirmPasswordError);
        // Display first/last name errors if needed (optional, TextField 'required' handles basic case)

        // Only submit if there are no errors
        if (currentPasswordError || currentEmailError || currentConfirmPasswordError || currentFirstNameError || currentLastNameError) {
             setRegistrationMessage('Please fix the errors above.'); // Generic error message
             setIsSubmitting(false); // Re-enable button
            return;
        }

        try {
            const response = await fetch(`${config.backendUrl}/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                // Send firstName and lastName instead of name
                body: JSON.stringify({ email, firstName, lastName, password, role_id: 3 }),
            });

            const data = await response.json();

            if (response.ok) {
                // Display success message instead of navigating immediately
                setRegistrationMessage('Registration successful! Please check your email to verify your account.');
                // Optionally clear the form or disable it further
                setEmail('');
                setFirstName('');
                setLastName('');
                setPassword('');
                setConfirmPassword('');
                // Keep isSubmitting true to prevent further submits after success
            } else {
                // Handle specific errors from backend, e.g., email already exists
                setRegistrationMessage(data.message || 'Registration failed. Please try again.');
                setIsSubmitting(false); // Re-enable button on failure
            }
        } catch (error) {
            console.error("An error occurred during registration:", error);
            setRegistrationMessage('An network error occurred during registration. Please try again.');
            setIsSubmitting(false); // Re-enable button on network error
        }
    };

    // --- Other handlers (handleClickShowPassword, etc.) remain mostly the same ---

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        setPasswordError(checkPasswordStrength(newPassword));
        // Also validate confirm password against new password
        if (confirmPassword && confirmPassword !== newPassword) {
            setConfirmPasswordError("Passwords do not match.");
        } else {
            setConfirmPasswordError("");
        }
    };

    const handleConfirmPasswordChange = (e) => {
        const newConfirmPassword = e.target.value;
        setConfirmPassword(newConfirmPassword);
        // Validate against current password
        if (password && newConfirmPassword !== password) { // Check if password has value
            setConfirmPasswordError("Passwords do not match.");
        } else {
            setConfirmPasswordError("");
        }
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);
        setEmailError(validateEmail(newEmail));
    };

    // Update disabled logic to include first/last name potentially
    const isRegisterButtonDisabled =
        isSubmitting || // Disable while submitting
        !firstName || // Disable if first name is empty
        !lastName ||  // Disable if last name is empty
        !email ||     // Disable if email is empty
        !password ||  // Disable if password is empty
        !confirmPassword || // Disable if confirm password is empty
        passwordError !== "" ||
        emailError !== "" ||
        confirmPasswordError !== "";

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                maxWidth: '500px', // Set a max width for the form
                margin: 'auto', // Center the box
                padding: 3,
                boxShadow: 3, // Add some shadow for visual separation
                borderRadius: 2, // Add rounded corners
                mt: 5 // Add margin top
            }}
        >
            <Typography variant="h5" component="h2" mb={3}> {/* Add margin bottom */}
                Register a Radian Account
            </Typography>

            {/* Display Registration Message */}
            {registrationMessage && (
                <Alert severity={registrationMessage.includes('successful') ? 'success' : 'error'} sx={{ width: '100%', mb: 2 }}>
                    {registrationMessage}
                </Alert>
            )}

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                <Grid container spacing={2}>
                    {/* First Name Field */}
                    <Grid item xs={12} sm={6}> {/* Use sm={6} for side-by-side on small screens */}
                        <TextField
                            label="First Name"
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                            fullWidth
                            variant="outlined" // Use outlined variant
                        />
                    </Grid>
                    {/* Last Name Field */}
                    <Grid item xs={12} sm={6}> {/* Use sm={6} for side-by-side */}
                         <TextField
                            label="Last Name"
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                    {/* Email Field */}
                    <Grid item xs={12}>
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={handleEmailChange}
                            required
                            error={!!emailError} // Use boolean conversion for error prop
                            helperText={emailError}
                            fullWidth
                            variant="outlined"
                        />
                    </Grid>
                     {/* Password Field */}
                    <Grid item xs={12}>
                        <TextField
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={handlePasswordChange}
                            required
                            error={!!passwordError}
                            helperText={passwordError}
                            fullWidth
                            variant="outlined"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    {/* Confirm Password Field */}
                    <Grid item xs={12}>
                        <TextField
                            label="Confirm Password"
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={handleConfirmPasswordChange}
                            required
                            error={!!confirmPasswordError}
                            helperText={confirmPasswordError}
                            fullWidth
                            variant="outlined"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle confirm password visibility"
                                            onClick={handleClickShowConfirmPassword}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    {/* Submit Button */}
                    <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isRegisterButtonDisabled}
                            size="large" // Make button larger
                        >
                            Register
                        </Button>
                    </Grid>
                </Grid>
            </form>
            {/* Link to Login */}
            <Box mt={3} textAlign="center"> {/* Increased margin */}
                <Typography variant="body2">
                    Already have an account? <Link to="/login">Login here.</Link>
                </Typography>
            </Box>
        </Box>
    );
}

export default Register;
